import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

interface SidebarState {
  isOpen: boolean
  expandedMenus: string[] // Track which submenus are expanded by their item id
  _hasHydrated: boolean
  toggle: () => void
  open: () => void
  close: () => void
  setOpen: (isOpen: boolean) => void
  toggleMenu: (menuId: string) => void
  isMenuExpanded: (menuId: string) => boolean
  setHasHydrated: (state: boolean) => void
}

/**
 * Sidebar state management store
 *
 * Manages the collapsed/expanded state of the sidebar navigation.
 * State is persisted to localStorage to maintain user preference across sessions.
 *
 * Desktop: Toggle between icon-only (64px) and full width (256px)
 * Mobile: Toggle between hidden and overlay with backdrop
 */
export const useSidebar = create<SidebarState>()(
  persist(
    (set, get) => ({
      // Default to expanded - will be overridden by persisted value if exists
      isOpen: true,

      // Track which submenus are expanded
      expandedMenus: [],

      // Track hydration state to prevent SSR mismatch
      _hasHydrated: false,

      toggle: () => set((state) => ({ isOpen: !state.isOpen })),

      open: () => set({ isOpen: true }),

      close: () => set({ isOpen: false }),

      setOpen: (isOpen: boolean) => set({ isOpen }),

      toggleMenu: (menuId: string) =>
        set((state) => ({
          expandedMenus: state.expandedMenus.includes(menuId)
            ? state.expandedMenus.filter((id) => id !== menuId)
            : [...state.expandedMenus, menuId],
        })),

      isMenuExpanded: (menuId: string) => get().expandedMenus.includes(menuId),

      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),
    }),
    {
      name: 'sidebar-storage',
      storage: createJSONStorage(() => localStorage),
      // Persist isOpen and expandedMenus, not the hydration flag
      partialize: (state) => ({
        isOpen: state.isOpen,
        expandedMenus: state.expandedMenus,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)
